import { useCallback, useMemo, useState } from "react";

export function useRowSelection<T>(
  items: T[],
  getId: (row: T) => string,
  isSelectable: (row: T) => boolean = () => true,
) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const selectableIds = useMemo(
    () => items.filter(isSelectable).map(getId),
    [items, getId, isSelectable],
  );

  const selectedOnPage = useMemo(
    () => selectableIds.filter((id) => selected.has(id)).length,
    [selectableIds, selected],
  );

  const allOnPageSelected =
    selectableIds.length > 0 && selectedOnPage === selectableIds.length;

  const someOnPageSelected = selectedOnPage > 0 && !allOnPageSelected;

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAllOnPage = useCallback(() => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        for (const id of selectableIds) next.delete(id);
      } else {
        for (const id of selectableIds) next.add(id);
      }
      return next;
    });
  }, [allOnPageSelected, selectableIds]);

  const clear = useCallback(() => setSelected(new Set()), []);

  const setSelectedIds = useCallback((ids: string[]) => {
    setSelected(new Set(ids));
  }, []);

  const selectedIds = useMemo(() => [...selected], [selected]);

  return {
    selected,
    selectedIds,
    selectedCount: selected.size,
    selectedOnPage,
    allOnPageSelected,
    someOnPageSelected,
    toggle,
    toggleAllOnPage,
    clear,
    setSelectedIds,
  };
}
